import { useState } from "react";
import localization from "../../../../config/localization";
import { BonusConsts } from "../../models/constants/bonusConsts";
import {
  BonusDetailPerDayResponseDetails,
  CommonBonusDetails,
  IncludeExcludeProvidersGamesDetails,
} from "../../models/responses/index";
import TransactionCard from "../../../../components/ui/TransactionCard";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import { SupportedLanguages } from "../../../../models/constants/supportedLanguages";
import ProviderGamesDrawer from "../BonusComponents/ProviderGamesDrawer";
import {
  checkAndActiveCashbackWinningBoost,
  onIncludeExcludeProviderGame,
  retrieveDateWiseBonusDetail,
} from "../../services/bonus.service";
import { Button } from "../../../../components/ui";
import MinimumOddsSection from "../BonusComponents/MinimumOddsSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faIndianRupeeSign,
} from "@fortawesome/free-solid-svg-icons";
import { FrequencyEnums } from "../../models/enums/frequencyEnum";
import { ExchangeEnum } from "../../models/enums/exchangeEnum";
import { DayConstShortMap } from "../../models/constants/dayConstShortMap";
interface CashbackWinningBoostBonusCardProps {
  item: CommonBonusDetails;
  onClaimed?: () => void;
}
export default function CashbackWinningBoostBonus({
  item,
  onClaimed,
}: CashbackWinningBoostBonusCardProps) {
  const [isOpen, setOpen] = useState(false);
  const { language } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [providerGames, setProviderGames] = useState<
    IncludeExcludeProvidersGamesDetails[]
  >([]);

  const [showAmountBreakup, setShowAmountBreakup] = useState(false);
  const [isAmountLoading, setIsAmountLoading] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [bonusDetail, setBonusDetail] =
    useState<BonusDetailPerDayResponseDetails | null>(null);

  const totalPages = Math.max(
    1,
    Math.ceil((bonusDetail?.details?.[0]?.total ?? 0) / 10),
  );

  const bonusTypeConfig: Record<string, { icon: string; label: string }> = {
    Cashback: {
      icon: "/icons/Bonus/ic-cashback-bonus.svg",
      label: localization.cashback,
    },
    "Winning Boost": {
      icon: "/icons/Bonus/ic-winning-boost.svg",
      label: localization.winning_boost,
    },
  };

  const EXCHANGE_SUB_VERTICAL_ICONS: Record<
    number,
    { icon: string; alt: string }
  > = {
    [ExchangeEnum.Exchange]: {
      icon: "/icons/Bonus/ic-bonus-exchange-vertical.svg",
      alt: ExchangeEnum[ExchangeEnum.Exchange],
    },
    [ExchangeEnum.FancyBet]: {
      icon: "/icons/Bonus/ic-bonus-fancy-bet-sub-vertical.svg",
      alt: ExchangeEnum[ExchangeEnum.FancyBet],
    },
    [ExchangeEnum.Premium]: {
      icon: "/icons/Bonus/ic-bonus-premium-sub-vertical.svg",
      alt: ExchangeEnum[ExchangeEnum.Premium],
    },
    [ExchangeEnum.BookMaker]: {
      icon: "/icons/Bonus/ic-bonus-bookmaker-sub-vertical.svg",
      alt: ExchangeEnum[ExchangeEnum.BookMaker],
    },
  };

  const subverticals =
    item.sportssubverticalname?.split(",").map((name) => name.trim()) ?? [];
  const hasBharatSportsbook = subverticals.includes(
    BonusConsts.BharatSportsBook,
  );
  const hasSportsbookPlus = subverticals.includes(BonusConsts.SportBookPlus);

  const handleProviderGamesClick = async () => {
    const details = await onIncludeExcludeProviderGame(item.bonusid);
    setProviderGames(details);
    setDrawerOpen(true);
  };

  const config = bonusTypeConfig[item.bonustype];
  const getStatusDateInfo = () => {
    switch (item.status) {
      case BonusConsts.ActiveStatus:
      case BonusConsts.InActiveStatus:
        return {
          label: localization.bonus_claiming_date,
          value: item.claimingdate
            ? formatUtcToLocalDateTime(item.claimingdate, false)
            : "-",
        };
      case BonusConsts.ConsumedStatus:
        return {
          label: localization.bonusconsumeddate,
          value: item.claimingdate
            ? formatUtcToLocalDateTime(item.claimingdate, false)
            : "-",
        };
      case BonusConsts.CancelledStatus:
        return {
          label: localization.bonuscanceldate,
          value: formatUtcToLocalDateTime(item.modified, false),
        };
      case BonusConsts.ExpiredStatus:
        return {
          label: localization.bonus_claiming_date,
          value: item.claimingdate
            ? formatUtcToLocalDateTime(item.modified, false)
            : "-",
        };
    }
  };
  const statusDate = getStatusDateInfo();
  const [isActivating, setIsActivating] = useState(false);

  const handleOptIn = async () => {
    if (isActivating) return;

    setIsActivating(true);
    try {
      const success = await checkAndActiveCashbackWinningBoost(
        item.bonuswallettransactionid,
      );
      if (success) {
        await onClaimed?.();
      }
    } finally {
      setIsActivating(false);
    }
  };

  function IsolatedLayout({
    promoCap,
    creditingTime,
    statusDate,
    bonusEndDate,
    status,
  }: {
    promoCap: number | null;
    creditingTime: string;
    statusDate: { value: string; label: string };
    bonusEndDate: string | null;
    status: string;
  }) {
    const creditingTimeValue = creditingTime;
    const hasPromoCap = promoCap;

    return (
      <>
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {statusDate?.value}
            </span>
            <p className="text-xs text-[#A9A9A9]">{statusDate?.label}</p>
          </div>
          {status !== BonusConsts.ExpiredStatus && (
            <div className="min-w-0">
              <span className="text-sm font-medium break-words">
                {bonusEndDate
                  ? formatUtcToLocalDateTime(bonusEndDate, false)
                  : "-"}
              </span>
              <p className="text-xs text-[#A9A9A9]">
                {localization.bonus_end_date}
              </p>
            </div>
          )}
        </div>
        <div
          className={`grid ${hasPromoCap ? "grid-cols-2" : "grid-cols-1"} gap-2`}
        >
          {hasPromoCap && (
            <div className="min-w-0">
              <span className="text-sm font-semibold break-words">
                {item.maxcashbackperbonuscampaign!.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />
              </span>
              <p className="text-xs text-[#A9A9A9]">
                {localization.bonus_promo_cap}
              </p>
            </div>
          )}
          <div className="min-w-0">
            <span className="text-sm font-semibold break-words">
              {creditingTimeValue}
            </span>
            <p className="text-xs text-[#A9A9A9]">
              {localization.bonus_crediting_time}
            </p>
          </div>
        </div>
      </>
    );
  }

  function DailyLayout({
    cycleCap,
    promoCap,
    creditingTime,
    statusDate,
  }: {
    cycleCap: number | null;
    promoCap: number | null;
    creditingTime: string;
    statusDate: { value: string; label: string };
  }) {
    return (
      <>
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {statusDate.value}
            </span>
            <p className="text-xs text-[#A9A9A9]">{statusDate.label}</p>
          </div>
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {item.bonusexpiredate
                ? formatUtcToLocalDateTime(item.bonusexpiredate, false)
                : "-"}
            </span>
            <p className="text-xs text-[#A9A9A9]">
              {item.status === BonusConsts.ExpiredStatus
                ? localization.bonusexpireddate
                : localization.bonusexpiringdate}
            </p>
          </div>
        </div>
        {(cycleCap || promoCap) && (
          <div
            className={`grid ${cycleCap && promoCap ? "grid-cols-2" : "grid-cols-1"} gap-2`}
          >
            {cycleCap && (
              <div className="min-w-0">
                <span className="text-sm font-medium break-words">
                  {cycleCap.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />
                </span>
                <p className="text-xs text-[#A9A9A9]">
                  {localization.bonus_cycle_cap}
                </p>
              </div>
            )}
            {promoCap && (
              <div className="min-w-0">
                <span className="text-sm font-medium break-words">
                  {promoCap.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />
                </span>
                <p className="text-xs text-[#A9A9A9]">
                  {localization.bonus_promo_cap}
                </p>
              </div>
            )}
          </div>
        )}
        <div className="min-w-0">
          <span className="text-sm font-semibold break-words">
            {creditingTime}
          </span>
          <p className="text-xs text-[#A9A9A9]">
            {localization.bonus_crediting_time}
          </p>
        </div>
      </>
    );
  }
  function WeeklyAndMonthlyLayout({
    cycleCap,
    promoCap,
    creditingTime,
    statusDate,
  }: {
    cycleCap: number | null;
    promoCap: number | null;
    creditingTime: string;
    statusDate: { value: string; label: string };
  }) {
    const statusPrimaryDate =
      item.status === BonusConsts.ConsumedStatus ||
        item.status === BonusConsts.CancelledStatus
        ? formatUtcToLocalDateTime(item.modified, false)
        : item.claimingdate
          ? formatUtcToLocalDateTime(item.claimingdate, false)
          : "-";

    const statusPrimaryLabel =
      item.status === BonusConsts.ConsumedStatus
        ? localization.bonusconsumeddate
        : item.status === BonusConsts.CancelledStatus
          ? localization.bonuscanceldate
          : localization.bonus_claiming_date;
    const statusSecondaryDate = item.bonusexpiredate
      ? formatUtcToLocalDateTime(item.bonusexpiredate, false)
      : "-";

    const statusSecondaryLabel =
      item.status === BonusConsts.ExpiredStatus
        ? localization.bonusexpireddate
        : localization.bonusexpiringdate;

    const cashbackPlayStartTime = formatUtcToLocalDateTime(
      item.starttime!,
      false,
    ).split(" ")[1];

    const cashbackPlayEndTime = formatUtcToLocalDateTime(
      item.endtime!,
      false,
    ).split(" ")[1];

    const cashbackPlayTimeRange = `${cashbackPlayStartTime} to ${cashbackPlayEndTime}`;
    return (
      <>
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {statusPrimaryDate}
            </span>
            <p className="text-xs text-[#A9A9A9]">{statusPrimaryLabel}</p>
          </div>
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {statusSecondaryDate}
            </span>
            <p className="text-xs text-[#A9A9A9]">{statusSecondaryLabel}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {cashbackPlayTimeRange}
            </span>
            <p className="text-xs text-[#A9A9A9]">
              {item.bonustype === BonusConsts.CashbackBonus
                ? localization.cashback_play_time
                : localization.winning_boost_play_time}
            </p>
          </div>
          <div className="min-w-0">
            <span className="text-sm font-medium break-words">
              {creditingTime}
            </span>
            <p className="text-xs text-[#A9A9A9]">
              {localization.bonus_crediting_time}
            </p>
          </div>
        </div>
        {(cycleCap || promoCap) && (
          <div
            className={`grid ${cycleCap && promoCap ? "grid-cols-2" : "grid-cols-1"} gap-2`}
          >
            {cycleCap && (
              <div className="min-w-0">
                <span className="text-sm font-medium break-words">
                  {cycleCap.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />
                </span>
                <p className="text-xs text-[#A9A9A9]">
                  {localization.bonus_cycle_cap}
                </p>
              </div>
            )}
            {promoCap && (
              <div className="min-w-0">
                <span className="text-sm font-medium break-words">
                  {promoCap.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />
                </span>
                <p className="text-xs text-[#A9A9A9]">
                  {localization.bonus_promo_cap}
                </p>
              </div>
            )}
          </div>
        )}
      </>
    );
  }
  const addOrdinalSuffix = (num: number): string => {
    if (num % 10 === 1 && num % 100 !== 11) return `${num}st`;
    if (num % 10 === 2 && num % 100 !== 12) return `${num}nd`;
    if (num % 10 === 3 && num % 100 !== 13) return `${num}rd`;
    return `${num}th`;
  };
  const getRepeatOnChips = (
    repeatOnCsv: string | null | undefined,
    repeatEvery: number,
  ): string[] => {
    if (!repeatOnCsv) return [];

    const values = repeatOnCsv
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    if (repeatEvery === FrequencyEnums.WeeklyFrequency) {
      return values.map((v) => DayConstShortMap[v] || v);
    }

    if (repeatEvery === FrequencyEnums.MonthlyFrequency) {
      return values
        .map((v) => Number.parseInt(v, 10))
        .filter((n) => Number.isFinite(n))
        .map((n) => addOrdinalSuffix(n));
    }

    return values;
  };
  const handleAmountWonClick = async (
    page: number = 1,
    isPagination: boolean = false,
  ) => {
    if (!isPagination) {
      const willOpen = !showAmountBreakup;
      setShowAmountBreakup(willOpen);
      if (!willOpen) return; // close case
    }

    setIsAmountLoading(true);
    setAmountError(null);

    const response = await retrieveDateWiseBonusDetail(item.bonusid, page);

    if (!response) {
      setAmountError("Unable to load amount details");
      setIsAmountLoading(false);
      return;
    }

    setBonusDetail(response);
    setCurrentPage(page);
    setIsAmountLoading(false);
  };
  const renderFrequencyLayout = () => {
    switch (item.repeatevery) {
      case FrequencyEnums.IsolatedFrequency:
        return (
          <IsolatedLayout
            promoCap={item.maxcashbackperbonuscampaign ?? null}
            creditingTime={
              item.possiblesettlementdate
                ? formatUtcToLocalDateTime(item.possiblesettlementdate, false)
                : "-"
            }
            statusDate={statusDate!}
            bonusEndDate={item.bonusenddate}
            status={item.status}
          />
        );
      case FrequencyEnums.DailyFrequency:
        return (
          <DailyLayout
            cycleCap={item.maxcashbackpercycle ?? null}
            promoCap={item.maxcashbackperbonuscampaign ?? null}
            creditingTime={
              item.possiblesettlementdate
                ? formatUtcToLocalDateTime(item.possiblesettlementdate, false)
                : "-"
            }
            statusDate={statusDate!}
          />
        );
      case FrequencyEnums.WeeklyFrequency:
      case FrequencyEnums.MonthlyFrequency:
        return (
          <WeeklyAndMonthlyLayout
            cycleCap={item.maxcashbackpercycle ?? null}
            promoCap={item.maxcashbackperbonuscampaign ?? null}
            creditingTime={
              item.possiblesettlementdate
                ? formatUtcToLocalDateTime(item.possiblesettlementdate, false)
                : "-"
            }
            statusDate={statusDate!}
          />
        );
    }
  };
  return (
    <>
      <TransactionCard onToggleChange={(value) => setOpen(value)}>
        <div className="grid gap-3 text-white min-w-0 overflow-hidden">
          <div className="flex justify-between items-center min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src={config.icon}
                alt={config.label}
                className="size-6 shrink-0"
              />
              <span className="font-medium text-sm text-[#ECECFF] truncate">
                {config.label}
              </span>
            </div>
            {item.status === BonusConsts.ActiveStatus ? (
              <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#010101] via-[#003D13] to-[#010101] rounded-md px-3 py-1.5 text-xs text-green-500 font-medium">
                <img
                  src="/icons/Bonus/ic-cbwb-activated.svg"
                  alt="Activated"
                  className="size-3.5"
                />
                <span className="text-xs text-[#22C55E] font-medium">
                  {localization.bonus_activated}
                </span>
              </span>
            ) : item.status === BonusConsts.InActiveStatus ? (
              <Button
                onClick={handleOptIn}
                disabled={isActivating}
                className="bg-gradient-to-r from-[#650687] to-[#4C0067] text-white text-xs font-medium rounded-md px-4 py-1.5 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {localization.opt_in}
              </Button>
            ) : null}
          </div>
          <div className="font-medium text-lg truncate">{item.bonusname}</div>
          {item.verticalbonusname && (
            <div className="flex flex-wrap gap-1.5">
              {item.verticalbonusname.split(",").map((tag, index) => {
                const name = tag.trim().toLowerCase();
                let chips: { src: string; label: string }[] = [];

                if (name.includes(BonusConsts.ExchangeVertical.toLowerCase())) {
                  chips = [
                    {
                      src: "/icons/Bonus/ic-bonus-exchange-vertical.svg",
                      label: tag.trim(),
                    },
                  ];
                } else if (
                  name.includes(BonusConsts.CasinoVertical.toLowerCase())
                ) {
                  chips = [
                    {
                      src: "/icons/Bonus/ic-bonus-casino-vertical.svg",
                      label: tag.trim(),
                    },
                  ];
                } else if (
                  name.includes(BonusConsts.SportsVertical.toLowerCase())
                ) {
                  chips = [
                    ...(hasBharatSportsbook
                      ? [
                        {
                          src: "/icons/Bonus/ic-bonus-sports-india-vertical.svg",
                          label: BonusConsts.SportsIndia.replace(" ", ""),
                        },
                      ]
                      : []),
                    ...(hasSportsbookPlus
                      ? [
                        {
                          src: "/icons/Bonus/ic-bonus-sports-vertical.svg",
                          label: BonusConsts.SportsVertical,
                        },
                      ]
                      : []),
                  ];
                }

                return chips.map((chip, i) => (
                  <span
                    key={`${index}-${i}`}
                    className="inline-flex items-center gap-1.5 bg-[#1A1A1A] border border-[#353535] rounded-lg px-2.5 py-1"
                  >
                    <img src={chip.src} alt={chip.label} className="size-4" />
                    <span className="text-xs text-white">
                      {chip.label === BonusConsts.SportsIndia.replace(" ", "") ? localization.sportsIndia : localization.getString(chip.label.toLowerCase())}
                    </span>
                  </span>
                ));
              })}

              {item.verticalbonusname
                .toLowerCase()
                .includes(BonusConsts.ExchangeVertical.toLowerCase()) &&
                item.exchangegameproviderids?.map((id) => {
                  const sub = EXCHANGE_SUB_VERTICAL_ICONS[id];
                  return sub && id !== ExchangeEnum.Exchange ? (
                    <span
                      key={`sub-${id}`}
                      className="inline-flex items-center gap-1.5 bg-[#1A1A1A] border border-[#353535] rounded-lg px-2.5 py-1"
                    >
                      <img src={sub.icon} alt={sub.alt} className="size-4" />
                      <span className="text-xs text-white">{sub.alt}</span>
                    </span>
                  ) : null;
                })}
            </div>
          )}
          <div className="pb-2 mb-2 pt-2 border-t border-b border-[#2A2A2A]">
            <div className="w-full flex items-center justify-between min-w-0">
              <span className="text-sm text-[#A9A9A9] shrink-0">
                {localization.amount_won}
              </span>

              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-white truncate no-underline">
                  {`${item.bonusamount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}₹`}
                </span>

                <Button
                  onClick={() => handleAmountWonClick()}
                  className="text-[#A9A9A9] hover:text-white transition no-underline opacity-70"
                  style={{ textDecoration: "none" }}
                  aria-label={
                    showAmountBreakup
                      ? "Collapse amount details"
                      : "Expand amount details"
                  }
                >
                  <FontAwesomeIcon
                    icon={showAmountBreakup ? faChevronUp : faChevronDown}
                    className="h-4 w-4"
                  />
                </Button>
              </div>
            </div>

            {showAmountBreakup && (
              <div className="mt-3 bg-[#010101] border border-[#353535] rounded-[4px]">
                {isAmountLoading ? (
                  <div className="px-4 py-3 text-sm text-[#A9A9A9]">
                    Loading...
                  </div>
                ) : amountError ? (
                  <div className="px-4 py-3 text-sm text-red-400">
                    {amountError}
                  </div>
                ) : (bonusDetail?.details?.length ?? 0) === 0 ? (
                  <div className="px-4 py-3 text-sm text-[#A9A9A9]">
                    {localization.no_bonus_details_available}
                  </div>
                ) : (
                  <>
                    {bonusDetail!.details.map((row, idx) => (
                      <div
                        key={`${row.date}-${idx}`}
                        className={`px-4 py-4 flex justify-between items-center ${idx !== bonusDetail!.details.length - 1
                          ? "border-b border-dashed border-[#4A4A4A]"
                          : ""
                          }`}
                      >
                        <span className="text-sm text-[#BFBFBF]">
                          {formatUtcToLocalDateTime(row.date, false)}
                        </span>
                        <span className="text-sm font-medium text-white">
                          {`${row.amount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}₹`}
                        </span>
                      </div>
                    ))}

                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A2A2A]">
                      <Button
                        disabled={currentPage <= 1 || isAmountLoading}
                        onClick={() =>
                          handleAmountWonClick(currentPage - 1, true)
                        }
                        className="text-xs px-3 py-1 rounded border border-[#353535] text-white disabled:opacity-40"
                      >
                        Prev
                      </Button>

                      <span className="text-xs text-[#A9A9A9]">
                        {currentPage} / {totalPages}
                      </span>

                      <Button
                        disabled={currentPage >= totalPages || isAmountLoading}
                        onClick={() =>
                          handleAmountWonClick(currentPage + 1, true)
                        }
                        className="text-xs px-3 py-1 rounded border border-[#353535] text-white disabled:opacity-40"
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {isOpen && (
            <div className="grid gap-3 text-white mt-2 min-w-0">
              {renderFrequencyLayout()}
              {(item.verticalbonusname
                ?.toLowerCase()
                .includes(BonusConsts.ExchangeVertical.toLowerCase()) ||
                item.verticalbonusname
                  ?.toLowerCase()
                  .includes(BonusConsts.SportsVertical.toLowerCase())) && (
                  <MinimumOddsSection
                    verticalbonusname={item.verticalbonusname}
                    isindividualminimumoddsenabled={
                      item.isindividualminimumoddsenabled
                    }
                    exchangeminimumoddsjson={item.exchangeminimumoddsjson}
                    minimumodds={item.minimumodds}
                  />
                )}
              {(item.repeatevery === FrequencyEnums.WeeklyFrequency ||
                item.repeatevery === FrequencyEnums.MonthlyFrequency) && (
                  <div className="mt-2">
                    {item.bonustype === BonusConsts.CashbackBonus && (
                      <p className="text-sm text-[#A9A9A9] pb-2 mb-2 border-b border-[#2A2A2A]">
                        {item.repeatevery === FrequencyEnums.WeeklyFrequency
                          ? localization.cashback_days
                          : localization.cashback_dates}
                      </p>
                    )}

                    {item.bonustype === BonusConsts.WinningBoostBonus && (
                      <p className="text-sm text-[#A9A9A9] pb-2 mb-2 border-b border-[#2A2A2A]">
                        {item.repeatevery === FrequencyEnums.WeeklyFrequency
                          ? localization.winning_boost_days
                          : localization.winning_boost_dates}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {getRepeatOnChips(item.repeatoncsv, item.repeatevery!).map(
                        (day) => (
                          <span
                            key={day}
                            className="inline-flex items-center bg-[#1A1A1A] border border-[#353535] rounded-md px-3 py-2.5 text-sm text-white"
                          >
                            {item.repeatevery ===
                              FrequencyEnums.MonthlyFrequency ? (
                              <>
                                {day.replace(/\D/g, "")}
                                <sup>{day.replace(/\d/g, "")}</sup>
                              </>
                            ) : (
                              day
                            )}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}

              <div className="min-w-0">
                <span className="text-sm font-medium break-words">
                  {language === SupportedLanguages.Hindi
                    ? (item.bonushindidescription ?? "-")
                    : (item.bonusdescription ?? "-")}
                </span>
                <p className="text-xs text-[#A9A9A9]">
                  {localization.bonusdescription}
                </p>
              </div>
              {item.verticalbonusname?.includes(BonusConsts.CasinoVertical) && (
                <Button
                  onClick={handleProviderGamesClick}
                  className="flex items-center justify-between w-fit bg-[#001F3A] rounded-lg px-2 py-2.5 text-[#8ECAFF] text-sm font-medium"
                >
                  <span>{localization.provider_and_games}</span>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="ml-1 text-xs"
                  />
                </Button>
              )}
            </div>
          )}
        </div>
      </TransactionCard>
      {item.verticalbonusname?.includes(BonusConsts.CasinoVertical) && (
        <ProviderGamesDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          details={providerGames}
        />
      )}
    </>
  );
}
